import {
  CloudDownloadRounded,
  CloudSyncRounded,
  StorageRounded
} from "@mui/icons-material";
import React, { useState } from "react";
import { FiArrowUpRight } from "react-icons/fi";
import { extractErrorMessage } from "../../common/utils/axios.isAxiosError";
import { saveBlob } from "../../common/utils/downloadBlob";
import BackupsModal from "../../components/modal/BackupsModal";
import CompanyExportAdminModal from "../../components/modal/CompanyExportAdminModal";
import CompanyImportModal from "../../components/modal/CompanyImportModal";
import PageToaster from "../../components/toaster/PageToaster";
import { useAuth } from "../../hooks/useAuth";
import { usePageToast } from "../../hooks/usePageToast";
import { exportCompanyBackup } from "../../services/companyBackup.service";
import {
  AmbientGlow,
  Container,
  Header,
  HeaderText,
  MainContent,
  NewPill,
  SectionLabel,
  ToolArrow,
  ToolBody,
  ToolCard,
  ToolGrid,
  ToolIcon
} from "./utilitarian.styled";

const accents = {
  indigo: "linear-gradient(135deg, #2b7bff 0%, #3b82f6 100%)",
  cyan: "linear-gradient(135deg, #22d3ee 0%, #2b7bff 100%)",
  violet: "linear-gradient(135deg, #7c5cff 0%, #3b82f6 100%)"
};

const Utilitarian: React.FC = () => {
  const [openBackups, setOpenBackups] = useState(false);
  const [openCompanyImport, setOpenCompanyImport] = useState(false);
  const [openCompanyExportAdmin, setOpenCompanyExportAdmin] = useState(false);
  const [exportingCompany, setExportingCompany] = useState(false);
  const { isAdmin, isManager, isOwner } = useAuth();

  const containerId = "toaster-utilitarian";
  const { autoSuccess, autoError } = usePageToast(containerId, true);

  const handleExportCompanyBackup = async () => {
    setExportingCompany(true);
    try {
      const blob = await exportCompanyBackup();
      saveBlob(blob, `backup_empresa_${Date.now()}.json`);
      autoSuccess("Backup da empresa gerado com sucesso.");
    } catch (err) {
      autoError(extractErrorMessage(err) || "Falha ao gerar o backup da empresa.");
    } finally {
      setExportingCompany(false);
    }
  };

  const canExportCompany = isManager || isOwner;

  return (
    <Container>
      <AmbientGlow />
      <MainContent>
        <Header>
          <HeaderText>
            <span className="eyebrow">Dados &amp; segurança</span>
            <h1>
              Central de <span className="accent">Backup</span>
            </h1>
            <p>
              Exporte, importe e restaure os dados da plataforma com total
              controle e segurança.
            </p>
          </HeaderText>
        </Header>

        {canExportCompany && (
          <>
            <SectionLabel>
              <h3>
                <CloudSyncRounded /> Minha empresa
              </h3>
              <small>Manager &amp; Owner</small>
            </SectionLabel>

            <ToolGrid>
              <ToolCard
                type="button"
                $accent={accents.cyan}
                $delay={0}
                disabled={exportingCompany}
                onClick={handleExportCompanyBackup}
              >
                <ToolIcon $accent={accents.cyan}>
                  <CloudDownloadRounded />
                </ToolIcon>
                <ToolBody>
                  <div className="title-row">
                    <h2>Exportar dados da empresa</h2>
                    <NewPill>Novo</NewPill>
                  </div>
                  <p>
                    {exportingCompany
                      ? "Gerando o arquivo, aguarde..."
                      : "Baixe um arquivo com todos os dados da sua empresa — sem misturar com as outras."}
                  </p>
                  <span className="meta">JSON · só a sua empresa</span>
                </ToolBody>
                <ToolArrow>
                  <FiArrowUpRight />
                </ToolArrow>
              </ToolCard>
            </ToolGrid>
          </>
        )}

        {isAdmin && (
          <>
            <SectionLabel>
              <h3>
                <StorageRounded /> Administração
              </h3>
              <small>Somente administrador</small>
            </SectionLabel>

            <ToolGrid>
              <ToolCard
                type="button"
                $accent={accents.indigo}
                $delay={0}
                onClick={() => setOpenBackups(true)}
              >
                <ToolIcon $accent={accents.indigo}>
                  <StorageRounded />
                </ToolIcon>
                <ToolBody>
                  <div className="title-row">
                    <h2>Backups do banco de dados</h2>
                  </div>
                  <p>
                    Gere, baixe e restaure backups completos do banco — inclui
                    restauração a partir de um arquivo enviado na hora.
                  </p>
                  <span className="meta">Banco completo</span>
                </ToolBody>
                <ToolArrow>
                  <FiArrowUpRight />
                </ToolArrow>
              </ToolCard>

              <ToolCard
                type="button"
                $accent={accents.violet}
                $delay={1}
                onClick={() => setOpenCompanyImport(true)}
              >
                <ToolIcon $accent={accents.violet}>
                  <CloudSyncRounded />
                </ToolIcon>
                <ToolBody>
                  <div className="title-row">
                    <h2>Reimportar empresa</h2>
                    <NewPill>Novo</NewPill>
                  </div>
                  <p>
                    Reinsira o backup JSON de uma empresa específica — útil
                    após uma limpeza ou um imprevisto.
                  </p>
                  <span className="meta">Por empresa</span>
                </ToolBody>
                <ToolArrow>
                  <FiArrowUpRight />
                </ToolArrow>
              </ToolCard>

              <ToolCard
                type="button"
                $accent={accents.cyan}
                $delay={2}
                onClick={() => setOpenCompanyExportAdmin(true)}
              >
                <ToolIcon $accent={accents.cyan}>
                  <CloudDownloadRounded />
                </ToolIcon>
                <ToolBody>
                  <div className="title-row">
                    <h2>Exportar empresa</h2>
                    <NewPill>Novo</NewPill>
                  </div>
                  <p>
                    Escolha qualquer empresa e baixe o backup JSON dela — sem
                    precisar logar como o manager/owner.
                  </p>
                  <span className="meta">Por empresa</span>
                </ToolBody>
                <ToolArrow>
                  <FiArrowUpRight />
                </ToolArrow>
              </ToolCard>
            </ToolGrid>
          </>
        )}
      </MainContent>

      <BackupsModal open={openBackups} onClose={() => setOpenBackups(false)} />
      <CompanyImportModal
        open={openCompanyImport}
        onClose={() => setOpenCompanyImport(false)}
      />
      <CompanyExportAdminModal
        open={openCompanyExportAdmin}
        onClose={() => setOpenCompanyExportAdmin(false)}
      />
      <PageToaster containerId={containerId} />
    </Container>
  );
};

export default Utilitarian;
